/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, ChangeEvent, useEffect } from 'react';
import {
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  ButtonGroup,
  LoadingButton,
  Button,
  Link,
  Typography,
  TextField,
  Alert,
} from '@one-thd/sui-atomic-components';
import { DRAWER_TEXT } from './otp-text-constants';
import {
  OTP_ERROR_SOURCES,
  OTP_ERROR_CODES,
  OTP_ERROR_MESSAGES,
  AUTH_REQUIRED_ERRORS,
  type OTPErrorSource
} from '../../utils/consts';
import { validateVerificationCode } from '../../utils/otp-utils';
import { getCustomerInfo } from '../../utils/general';
import { useGiftCardBarcodeService } from '../../services/gift-card-barcode-service';
import { IGiftCardCommonData } from '../../models/common-models';
import { IApiHeaders, createAuthHeaders } from '../../utils/api-headers';

interface ButtonConfig {
  inline: {
    text: string;
    channel: string;
  };
  separate?: {
    text: string;
    channel: string;
  };
}

interface DrawerTexts {
  title: string;
  header: string;
  verificationCode: string;
  verifyButton: string;
  cancelButton: string;
  refreshCode: string;
}

interface OTPDrawerContentProps {
  drawerTexts: DrawerTexts;
  contactMethodToUse: string;
  verificationCode: string;
  validationError: string | null;
  buttonsConfig: ButtonConfig;
  otpErrorMessage?: string;
  otpErrorSource?: OTPErrorSource | null;
  onVerificationCodeChange: (value: string) => void;
  onInlineButtonClick: () => void;
  onSeparateButtonClick: () => void;
  onCloseClick: () => void;
  isVerifyDisabled: boolean;
  isLoading: boolean;
  // New props for combined OTP verification and barcode retrieval
  shouldShowOTPContent: boolean;
  selectedGiftCardPaymentIDs: string[];
  otpHook: {
    userEmail: string | null;
    authID: string | null;
    setValidationError: (error: string) => void;
    setOTPDisabled: (disabled: boolean) => void;
    setOtpError: (message: string, source: OTPErrorSource) => void;
    clearOtpError: () => void;
    lastUsedOtpChannel: string;
    requestNewCodeWithChannel: (channel: string, isRetryFlow?: boolean) => Promise<void>;
  };
  onOTPVerified: (verified: boolean) => void;
  onBarcodeDataReceived: (barcodeData: Partial<IGiftCardCommonData>[]) => void;
  setIsBarcodeLoading: (loading: boolean) => void;
}

/**
 * Pure component for rendering OTP drawer content
 * Contains only the UI logic, no business logic
 */
export const OTPDrawerContent: FC<OTPDrawerContentProps> = ({
  drawerTexts,
  contactMethodToUse,
  verificationCode,
  validationError,
  buttonsConfig,
  otpErrorMessage,
  otpErrorSource,
  onVerificationCodeChange,
  onInlineButtonClick,
  onSeparateButtonClick,
  onCloseClick,
  isVerifyDisabled,
  isLoading,
  shouldShowOTPContent,
  selectedGiftCardPaymentIDs,
  otpHook,
  onOTPVerified,
  onBarcodeDataReceived,
  setIsBarcodeLoading
}) => {
  const { getGiftCardBarcodes, error, isGetGiftCardBarcodeLoading } = useGiftCardBarcodeService();
  const { customerAccountId, jwt } = getCustomerInfo();

  const handleVerifyClick = async () => {
    // Check if we should use the combined flow (OTP verification + barcode retrieval)
    if (shouldShowOTPContent) {
      // Clear retry error when user attempts to verify (they're using the new code)
      otpHook.clearOtpError();

      // Combined flow: validate OTP and call getGiftCardBarcode
      const codeValidation = validateVerificationCode(verificationCode);
      if (!codeValidation.isValid) {
        otpHook.setValidationError(codeValidation.error || 'Invalid verification code');
        return;
      }

      // Validate required authentication information
      if (!otpHook.userEmail || !otpHook.authID || !customerAccountId) {
        otpHook.setOtpError(OTP_ERROR_MESSAGES.MISSING_DATA, OTP_ERROR_SOURCES.VERIFY);
        return;
      }

      try {
        // Set loading state before API call
        setIsBarcodeLoading(true);

        // Call getGiftCardBarcode with OTP verification input
        const result = await getGiftCardBarcodes(
          selectedGiftCardPaymentIDs,
          {
            email: otpHook.userEmail,
            encryptedPayload: otpHook.authID,
            otp: verificationCode
          }
        );

        if (result.success && result.barcodeData) {
          // Successfully retrieved barcode data
          onBarcodeDataReceived(result.barcodeData);
          onOTPVerified(true);

          // Clear verification code only on success
          onVerificationCodeChange('');
        } else if (result.otpVerified === false) {
          // Handle OTP verification failure - these are validation errors
          // Clear verification code when OTP verification fails
          onVerificationCodeChange('');

          // Check if this is a max attempts error
          const isMaxAttemptsError = result.errorCode
            && OTP_ERROR_CODES.MAX_ATTEMPTS_EXCEEDED.includes(result.errorCode);

          if (isMaxAttemptsError) {
            // Handle max attempts exceeded
            otpHook.setValidationError('Send new OTP Code, Maximum attempts reached');
            otpHook.setOTPDisabled(true);
          } else if (result.remainingCount !== undefined) {
            // Handle invalid code with remaining attempts
            otpHook.setValidationError('Invalid code. Resend or try a different code.');
          } else {
            // Handle other verification failures
            otpHook.setValidationError(result.error || 'Invalid verification code');
          }
        } else {
          // Check if this is a retry error from the error response
          const isRetryError = result.errorCode
            && OTP_ERROR_CODES.RETRY_WITH_NEW_CODE.includes(result.errorCode);

          if (isRetryError) {
            // Show retry message and trigger new OTP code request
            otpHook.setOtpError(OTP_ERROR_MESSAGES.RETRY_WITH_NEW_CODE, OTP_ERROR_SOURCES.VERIFY);
            onVerificationCodeChange('');

            // Trigger new OTP code request with last used channel (pass true for isRetryFlow)
            await otpHook.requestNewCodeWithChannel(otpHook.lastUsedOtpChannel, true);
            return;
          }
          // Set general error for unhandled/unexpected errors
          otpHook.setOtpError(OTP_ERROR_MESSAGES.GENERAL_FAILURE, OTP_ERROR_SOURCES.VERIFY);
        }
      } catch (caughtError) {
        // Handle exceptions during the API call (network errors, etc.)
        otpHook.setOtpError(OTP_ERROR_MESSAGES.GENERAL_FAILURE, OTP_ERROR_SOURCES.VERIFY);
      } finally {
        // Clear loading state after API call completes
        setIsBarcodeLoading(false);
      }
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onVerificationCodeChange(event.target.value);
  };

  useEffect(() => {
    const isAuthError = otpErrorMessage && AUTH_REQUIRED_ERRORS.includes(otpErrorMessage);
    if (isGetGiftCardBarcodeLoading || !otpHook?.userEmail || isAuthError) {
      return;
    }

    if (error) {
      otpHook.setOtpError(OTP_ERROR_MESSAGES.GENERAL_FAILURE, OTP_ERROR_SOURCES.VERIFY);
    }

    setIsBarcodeLoading(false);
  }, [error, isGetGiftCardBarcodeLoading]);

  const handleSignOutClick = async () => {
    try {
      // Reuse centralized header creation for auth APIs
      const headers: IApiHeaders = createAuthHeaders(jwt || '');

      const response = await fetch('/customer/auth/v1/logout', {
        method: 'GET',
        headers,
        mode: 'cors'
      });

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.error('failed to sign out');
      } else {
        await response.json();
        // Only reload on successful logout
        window.location.reload();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error when trying to log out:', err);
      // Silently handle logout errors
    }
  };

  // For getOTPOptions or getOTPCode errors, show only the Alert error
  // if (otpErrorMessage && (otpErrorSource === OTP_ERROR_SOURCES.OPTIONS ||
  //     otpErrorSource === OTP_ERROR_SOURCES.CODE)) {
  if (otpErrorMessage && (otpErrorSource === OTP_ERROR_SOURCES.OPTIONS)) {
    const isAuthRequiredError = AUTH_REQUIRED_ERRORS.includes(otpErrorMessage);
    return (
      <>
        <DrawerHeader title="Verification Required" />
        <DrawerBody>
          <Alert status="error" size="small" data-testid="otp-error">
            {isAuthRequiredError ? (
              <div className="sui-flex sui-flex-col">
                <div className="sui-mb-4">
                  <Typography>{otpErrorMessage}</Typography>
                </div>
                <Link component="button" align="left" underline="always" onClick={handleSignOutClick}>
                  Click here to login
                </Link>
              </div>
            ) : (
              otpErrorMessage
            )}
          </Alert>
        </DrawerBody>
        <DrawerFooter>
          <Button
            variant="secondary"
            onClick={onCloseClick}
          >
            Close
          </Button>
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerHeader title={drawerTexts.title} />
      <DrawerBody>
        <>
          {otpErrorMessage && (
            <Alert status="error" size="large" data-testid="otp-error">
              {otpErrorMessage}
            </Alert>
          )}
          <Typography variant="body-base">
            {drawerTexts.header}
          </Typography>
          <Typography variant="body-base" weight="bold">
            {contactMethodToUse}
          </Typography>
          <div className="sui-my-4">
            <TextField
              placeholder={DRAWER_TEXT.CODE_PLACEHOLDER}
              value={verificationCode}
              type="text"
              pattern="[0-9]*"
              onChange={handleInputChange}
              label={drawerTexts.verificationCode}
              status={validationError ? 'error' : ''}
              statusMessage={validationError}
              id="otp-input"
              required
              autoFocus
            />
          </div>
          <div className="sui-flex sui-items-center sui-gap-2 sui-py-4">
            <Typography>
              {drawerTexts.refreshCode}
            </Typography>
            <Link
              component="button"
              onClick={onInlineButtonClick}
            >
              {buttonsConfig.inline.text}
            </Link>
          </div>
          {buttonsConfig.separate && (
            <Link
              component="button"
              onClick={onSeparateButtonClick}
            >
              {buttonsConfig.separate.text}
            </Link>
          )}
        </>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical" variant="primary">
          <LoadingButton
            variant="primary"
            onClick={handleVerifyClick}
            disabled={isVerifyDisabled}
            loading={isLoading}
          >
            {drawerTexts.verifyButton}
          </LoadingButton>
          <Button
            variant="secondary"
            onClick={onCloseClick}
          >
            {drawerTexts.cancelButton}
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};
